import * as Sentry from '@sentry/react';
import { Amplify } from 'aws-amplify';
import { dequal } from 'dequal';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { SWRConfig } from 'swr';

// Keep above ./app.tsx: `Sentry.init` must run before the app module graph is evaluated, or the router
// tracing wrapper degrades to the unwrapped `createBrowserRouter` and navigation spans are never emitted.
import './sentry.ts';

import { App } from './app.tsx';
import './index.css';
import { getCognitoClientId, getCognitoDomain, getCognitoUserPoolId } from './utils/env-utils.ts';
import { isPermissionError } from './utils/swr-utils.ts';

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: getCognitoUserPoolId(),
            userPoolClientId: getCognitoClientId(),
            loginWith: {
                email: true,
                oauth: {
                    redirectSignIn: [globalThis.location.origin],
                    redirectSignOut: [globalThis.location.origin],
                    domain: getCognitoDomain(),
                    providers: ['Google'],
                    scopes: [],
                    responseType: 'code',
                },
            },
        },
    },
});

const container = document.querySelector('#root');
const root = createRoot(container!, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
    <StrictMode>
        <SWRConfig
            value={{
                compare: dequal, // TODO: check performance impact
                // 401/403 won't resolve by retrying, so skip the retry loop for them (which would
                // otherwise re-fire onError on a backoff loop and stack the same notification). SWR's
                // default backoff + dedupe retry is left untouched for every other error.
                shouldRetryOnError: (error) => !isPermissionError(error),
            }}
        >
            <App />
        </SWRConfig>
    </StrictMode>
);
